/** @file Translate drag: every bone moves by the gizmo origin's (snapped, constrained) world delta. */
import { dragModifiers } from '@pose2d/editor-geometry/dragModifiers';
import { dragTranslate } from '@pose2d/editor-geometry/dragTranslate';
import { gizmoHandles } from '@pose2d/editor-geometry/gizmoHandles';
import { parentSpace } from '@pose2d/editor-geometry/parentSpace';
import type { TranslatePart } from '@pose2d/editor-geometry/types';
import type { Id } from '@pose2d/format/types';
import type { PreviewValues } from '../../panels/viewport/scene/channelOffset';
import type { ToolGesture } from '../../panels/viewport/viewportTypes';
import { worldAt } from '../../panels/viewport/worldAt';
import { boneOrigin } from './boneOrigin';
import { parentWorldOf } from './parentWorldOf';
import type { GestureStart } from './transformPress';
import { transformCommands } from './transformCommands';

/** Axes are world-aligned (R-E1-08); local x/y come from each bone's parent world at press time. */
export function translateGesture({ ctx, edit, bones, part, press }: GestureStart): ToolGesture {
  const origins = bones.map((i) => boneOrigin(ctx.pose, i));
  const parents = bones.map((i) => parentWorldOf(ctx.pose, ctx.data, i));
  const origin = origins.at(-1) ?? [0, 0];
  const drag = { part: part as TranslatePart, rotation: 0, origin, start: worldAt(ctx, press) };
  let values = new Map<Id, PreviewValues>();
  return {
    move(p) {
      const mods = dragModifiers(ctx.snap, p);
      const next = dragTranslate(drag, worldAt(ctx, p), { grid: ctx.grid, snap: mods.snap, constrain: mods.constrain }, [0, 0]);
      values = new Map(bones.map((i, k) => {
        const o = origins[k] ?? origin;
        const local = parentSpace.point(parents[k] ?? null, o[0] + next[0] - origin[0], o[1] + next[1] - origin[1], [0, 0]);
        return [ctx.maps.boneIds[i] ?? '', { x: local[0], y: local[1] }];
      }));
      ctx.setPreview(values);
      ctx.setGizmo({ handles: gizmoHandles('translate', { camera: ctx.camera, origin: next, rotation: 0 }), active: drag.part, points: [], lines: [] });
    },
    release: () => transformCommands(edit, values),
    cancel() {},
  };
}
